/**
 * What a chat send asked for, added to `new_conversation` and `send_message`: the GPT it went
 * to and the tools the user switched on. These are the switches, not what the model ended up
 * calling — every call it makes is its own `tool_called`.
 */
import type {
  CreateChatData,
  SendMessageData,
} from '@/apiClient/queries/chatQueryTypes'
import { findGptListItemInCaches } from '@/apiClient/queries/gptCacheUtils'
import { mcpConnectorQueryKeys } from '@/apiClient/queries/useMcpConnectorQueries'
import type { IMcpConnectionList } from '@/apiClient/types/mcpConnectors'
import { queryClient } from '@/lib/queryClient'
import {
  FILE_GENERATION_JOB_TYPES,
  FILE_GENERATION_JOBS,
} from '@/utils/fileGenerationJobs'

/** The chat page has already loaded the GPT, so its name is in the query cache. */
const resolveGptName = (data: CreateChatData | SendMessageData) => {
  if (data.gpt_object) return data.gpt_object.name
  if (!data.gpt_id) return null
  return findGptListItemInCaches(data.gpt_id)?.name ?? null
}

/** Connector slugs (`jira`, `outlook`, …) for the connection ids the send carried. */
const resolveConnectorSlugs = (connectionIds: string[] = []) => {
  const connections =
    queryClient.getQueryData<IMcpConnectionList>(
      mcpConnectorQueryKeys.connections(),
    )?.connections ?? []
  return connectionIds.flatMap((id) => {
    const slug = connections.find((connection) => connection.id === id)?.slug
    return slug ? [slug] : []
  })
}

export const chatSendEventProperties = (
  data: CreateChatData | SendMessageData,
) => ({
  chat_type: data.gpt_id || data.gpt_object ? 'gpt' : 'regular',
  gpt_id: data.gpt_id ?? null,
  gpt_name: resolveGptName(data),
  web_search: !!data.web_search,
  generate_image: !!data.generate_image,
  file_generation:
    FILE_GENERATION_JOB_TYPES.find(
      (jobType) => data[FILE_GENERATION_JOBS[jobType].requestField],
    ) ?? null,
  connectors: resolveConnectorSlugs(data.mcp_connector_ids),
})
